import { useEffect, useRef, useState } from 'react';
import { callAction } from '../../app/actions';
import { useAppStore } from '../../store';
import { canDocBtn, canEditTags, buildHiddenSets, isPermHidden } from '../../lib/permissions';
import { searchActive, searchMatchIds } from '../../lib/search';
import { DOC_KINDS, docIconId, docPermOnNode, kindColorOf, kindLabelOf } from '../../lib/docs';
import { nodeTint } from '../../lib/color';
import { SEARCH_DOC_KINDS, SEARCH_SCOPE_KEYS, type SearchDocKind, type SearchScopeKey } from '../../lib/location';
import { searchDocs, type DocSearchRow } from '../../api/search';
import { TagTree } from '../tags/TagTree';
import type { SearchScope, TagId } from '../../lib/types';

/**
 * The centre of the top bar: the search box, its scope panel, the match count, the TAGS
 * DROPDOWN behind the dot button (plan 2.3) — and, since plan 2.2, the DOC-CONTENT RESULTS
 * dropdown under the box.
 *
 * THE INPUT IS CONTROLLED, and that needed something to change on the other side. The vanilla
 * box was uncontrolled — the typed text lived in the DOM and nowhere else, and `applySearch()`
 * read it back out with `searchInput.value`. `restoreViewState()` then wrote into the element
 * directly, which is why the restore had to happen after the DOM existed.
 *
 * A controlled input needs the text to be state, so the app keeps `searchText` (raw, exactly
 * as typed) beside `searchQuery` (trimmed and lowercased, which is what every filter matches
 * against). Two fields rather than one is the point: binding the box to `searchQuery` would
 * rewrite what somebody typed — "Ab" would appear as "ab" — and move their caret. Bound to
 * `searchText`, the value React sets is character-for-character what the user typed, so React
 * writes nothing back and the caret never moves.
 *
 * ── THE SEARCH'S TWO HALVES (plan 2.2) ───────────────────────────────────────────────────
 * The scope panel's checkboxes are Name / Description / Tags / DOCUMENT TEXT — the
 * `SEARCH_SCOPE_KEYS` vocabulary deep links mint, so a `?scope=` URL and this panel cannot
 * drift. Below them, "Document type" narrows the whole search to ONE doc kind: while a kind
 * is selected the text scopes are out of play (greyed, with a hint saying so) and a hit is a
 * document of that kind, nothing else. The old presence facets are gone — Phase 4's heatmap
 * answers "which nodes have documentation" properly.
 *
 * Doc-content matching is the server's (`GET /api/search/docs`, same substring semantics as
 * the map): a 300 ms debounced fetch keyed on (query, content-scope, kind, locale), a
 * sequence counter so a stale response is dropped rather than applied, the FULL result rows
 * kept component-local for the dropdown, and only the owner-node ids handed to the app
 * (`setSearchContentHits`) so the map lights the owning nodes. The rows render under the box:
 * type icon, title, owning node, and the server's word-snapped snippet with the match bold
 * via hlStart/hlLen — never re-searched here, where a shorter earlier occurrence could win.
 *
 * ── THE TAGS DROPDOWN (plan 2.3) ─────────────────────────────────────────────────────────
 * The whole tag pane — the tree, the clear button, the add-a-tag footer — lives here as
 * `#tag-panel`, opened by the dot button `#tag-filter-btn`. `TagTree` is reused VERBATIM as
 * a child component (a second `createRoot` cannot nest inside another root's host); the
 * panel's body div keeps `id="tag-tree"` and satisfies TagTree's `host` prop, so the
 * drop-to-top-level surface and every spec selector survive the move.
 *
 * ONE `openPanel` FIELD holds which dropdown is open — scope panel, tags panel or results —
 * so they are mutually exclusive by construction, and one shared outside-click handler
 * closes whichever is up. The PIN is the exception: a pinned tags panel ignores outside
 * clicks, so reorganising tags does not fight the map. Pin state is transient — per session.
 *
 * The DOT BUTTON is the tag filter's visibility: one small circle per selected tag in that
 * tag's colour (up to three, then a compact "+N"), a single neutral ring when none.
 */

/** The four text scopes, in display order — the deep-link vocabulary verbatim (plan 1.6). */
const TEXT_SCOPES = SEARCH_SCOPE_KEYS;
const SCOPE_LABEL: Record<SearchScopeKey, string> = {
  name: 'Name',
  desc: 'Description',
  tags: 'Tags',
  content: 'Document text',
};

/** A kind's label comes from the one slot table; `custom` is not a slot and names itself. */
const kindLabel = (k: SearchDocKind): string =>
  DOC_KINDS.find((d) => d.perm === k)?.label ?? 'Custom';

/** How many of the selected tags get their own dot before the button compacts to "+N". */
const MAX_DOTS = 3;

/** The match counter reports SEARCH hits only — tag/status filters were never counted. */
const NO_TAGS: ReadonlySet<TagId> = new Set();

/** Under this many typed characters the server answers empty, so nothing is asked. */
const MIN_QUERY = 2;
/** Node rows shown before the list is cut off. The map is the place to see all of them. */
const MAX_NODE_ROWS = 6;
const FETCH_DEBOUNCE_MS = 300;

type OpenPanel = 'filter' | 'tags' | 'results' | null;

export function TopbarSearch({ host }: { host: HTMLElement }) {
  // One subscription per value. `nodes`/`edges`/`tags`/`tagEdges` are containers the store
  // adopts wholesale, so their identity changes exactly when their contents do — see the
  // container-identity contract in `store/slices/graph.ts`.
  const nodes = useAppStore((s) => s.nodes);
  const edges = useAppStore((s) => s.edges);
  const tags = useAppStore((s) => s.tags);
  const tagEdges = useAppStore((s) => s.tagEdges);
  const perm = useAppStore((s) => s.perm);
  const editing = useAppStore((s) => s.editing);
  const isLight = useAppStore((s) => s.isLight);
  const searchText = useAppStore((s) => s.searchText);
  const searchQuery = useAppStore((s) => s.searchQuery);
  const scope = useAppStore((s) => s.searchScope);
  const activeTags = useAppStore((s) => s.activeTags);
  const searchDocKind = useAppStore((s) => s.searchDocKind);
  const contentHits = useAppStore((s) => s.searchContentHits);
  const docTitles = useAppStore((s) => s.docTitles);
  const docTypes = useAppStore((s) => s.docTypes);
  const locale = useAppStore((s) => s.currentLocale);

  const [openPanel, setOpenPanel] = useState<OpenPanel>(null);
  // Pinned = the tags panel ignores outside clicks. Transient per session, on purpose.
  const [tagsPinned, setTagsPinned] = useState(false);
  // The tag tree needs its host ELEMENT (it is the drop surface for "move to top level"),
  // so the body div is state rather than a ref — the child mounts once the div exists.
  const [tagTreeHost, setTagTreeHost] = useState<HTMLDivElement | null>(null);

  // The doc-content results, component-local: the dropdown is the only consumer of the full
  // rows — the app-wide part of the answer is the owner-node ids, and those go through
  // `setSearchContentHits`. `rows === null` means "nothing answered yet", which is what
  // separates the loading state from an honest "No documents match".
  const [rows, setRows] = useState<DocSearchRow[] | null>(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const fetchSeq = useRef(0);

  const inputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const filterBtnRef = useRef<HTMLButtonElement>(null);
  const tagPanelRef = useRef<HTMLDivElement>(null);
  const tagBtnRef = useRef<HTMLButtonElement>(null);
  const tagNewRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    host.classList.add('tb-center');
  }, [host]);

  /**
   * Is there a SERVER question to ask — does any document search apply?
   *
   * Unchanged, and still what gates the fetch. What changed in V0.46 is that it is no
   * longer the same thing as "is there a dropdown": node hits are local and need nobody.
   */
  const fetchEligible =
    searchQuery.length >= MIN_QUERY && (!!scope.content || !!searchDocKind);

  // Built once per render, not once per node: the match pass asks isPermHidden for every
  // node on the map, and rebuilding the sets inside that callback would make the counter
  // quadratic in a path that runs on every keystroke.
  const hidden = buildHiddenSets(perm, tagEdges);

  const active = searchActive({ searchQuery, searchScope: scope });
  const nodeHits = active
    ? searchMatchIds({
        nodes,
        edges,
        tags,
        tagEdges,
        searchQuery,
        searchScope: scope,
        searchDocKind,
        contentHits,
        // SEARCH hits only — the tag and pipeline-stage filters highlight separately and
        // were never counted here either.
        activeTags: NO_TAGS,
        activeDocStatus: null,
        isPermHidden: (id) => isPermHidden(id, edges, hidden),
        editing,
        // The field's historical name; what it feeds is the invisible-tag reveal, which is
        // the tag-structure brick's since V0.37. See FilterState.
        canEdit: canEditTags(perm),
      })
    : [];
  const matches = nodeHits.length;
  const nodeRows = nodeHits.slice(0, MAX_NODE_ROWS);

  /**
   * Is there a dropdown at all?
   *
   * Two independent reasons for one to exist (V0.46): nodes this query matched, answered
   * from the map already in memory, and documents, answered by the server. Either is enough;
   * neither on its own used to be.
   */
  const resultsEligible =
    searchQuery.length >= MIN_QUERY && (fetchEligible || nodeHits.length > 0);

  // ONE outside-click handler for every dropdown — the same idiom the dotted-link menu and
  // the About panel use. A pinned tags panel is the deliberate exception.
  useEffect(() => {
    if (!openPanel) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node | null;
      if (!t) return;
      if (openPanel === 'filter') {
        if (panelRef.current?.contains(t) || filterBtnRef.current?.contains(t)) return;
      } else if (openPanel === 'tags') {
        if (tagPanelRef.current?.contains(t) || tagBtnRef.current?.contains(t)) return;
        if (tagsPinned) return;
      } else {
        // Clicking back into the box is part of using the results, not a dismissal.
        if (resultsRef.current?.contains(t) || inputRef.current?.contains(t as Node)) return;
      }
      setOpenPanel(null);
    };
    document.addEventListener('click', onDown);
    return () => document.removeEventListener('click', onDown);
  }, [openPanel, tagsPinned]);

  // The dropdown cannot outlive its reason: clearing the box (Escape included), or a query
  // that now matches no node AND asks the server nothing, closes it.
  useEffect(() => {
    if (!resultsEligible) setOpenPanel((o) => (o === 'results' ? null : o));
  }, [resultsEligible]);

  // THE FETCH. Debounced behind the keystrokes; re-keyed on everything that changes the
  // question (query, content scope, kind, locale). The sequence counter is what drops a
  // stale response — StrictMode's double effects and out-of-order networks both land on it.
  useEffect(() => {
    if (!fetchEligible) {
      fetchSeq.current += 1; // orphan anything in flight
      setRows(null);
      setTotal(0);
      setLoading(false);
      return;
    }
    const q = searchQuery;
    const seq = ++fetchSeq.current;
    setLoading(true);
    const timer = setTimeout(() => {
      void (async () => {
        try {
          const resp = await searchDocs(q, { kind: searchDocKind, locale });
          if (seq !== fetchSeq.current) return; // a newer question owns the dropdown now
          setRows(resp.results);
          setTotal(resp.total);
          setLoading(false);
          // Only the owner-node ids go app-wide. The action re-checks the query (the map's
          // own staleness guard) and NEVER calls applySearch — see its header.
          callAction('setSearchContentHits', resp.results.map((r) => r.nodeId), q);
        } catch {
          if (seq !== fetchSeq.current) return;
          setRows([]);
          setTotal(0);
          setLoading(false);
        }
      })();
    }, FETCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [fetchEligible, searchQuery, searchDocKind, locale]);

  const allOn = TEXT_SCOPES.every((k) => scope[k]);

  const setScope = (next: SearchScope) => callAction('setSearchScope', next);

  const toggleText = (key: SearchScopeKey) => {
    const next = { ...scope, [key]: !scope[key] };
    // Turning the last one off would leave a box that can never match; the vanilla panel
    // handled this by re-checking the box under the pointer, which reads as the click having
    // been refused rather than silently inverted.
    if (!TEXT_SCOPES.some((k) => next[k])) return;
    setScope(next);
  };

  const toggleAll = () => {
    const on = !allOn;
    setScope({
      // Name stays on either way: turning All off would otherwise leave every text scope
      // off, which is a search that can never match. The vanilla panel did the same.
      name: true,
      desc: on,
      tags: on,
      content: on,
    });
  };

  const clear = () => {
    callAction('setSearchText', '', { frame: true });
    inputRef.current?.focus();
  };

  /** Typing and focusing surface the results whenever they are (or become) askable. */
  const surfaceResults = (nextText: string) => {
    // Optimistic: this runs on the keystroke, before the hits for the NEXT text exist. The
    // effect above closes it again on the render that finds nothing to put in it.
    const eligible = nextText.trim().length >= MIN_QUERY;
    setOpenPanel((o) => (eligible ? 'results' : o === 'results' ? null : o));
  };

  /**
   * A node result: go to it on the map.
   *
   * `revealNodeOnMap` rather than `centerOnNodes`, for the reason that function exists —
   * centring silently declines on a node inside a folded branch, which is most of them when
   * you have just searched for something you could not find.
   */
  const openNodeRow = (id: string) => {
    callAction('revealNodeOnMap', id);
    setOpenPanel(null);
  };

  const openRow = (r: DocSearchRow) => {
    // The client's own catalogue is locale-correct for the session; the row's title is the
    // fallback for a page the catalogue has not met (it was good enough to match on).
    const title = docTitles[r.docId] || r.title || 'Documentation';
    // Via the node (V0.47): the camera centres it, then the node itself crosses into the page.
    callAction('openDocFromNode', r.nodeId, { docId: r.docId, title }, 'current');
    setOpenPanel(null);
  };

  // The add-a-tag footer — the exact behaviour chrome.ts wired onto the sidebar's footer
  // before the pane moved here: trim, ignore empty, clear the field, keep focus for the next.
  const addTag = () => {
    const el = tagNewRef.current;
    if (!el) return;
    const v = el.value.trim();
    if (!v) return;
    void callAction('addTag', v, null);
    el.value = '';
    el.focus();
  };

  const selected = [...activeTags];
  const dotted = selected.slice(0, MAX_DOTS);
  const overflowCount = selected.length - dotted.length;

  const togglePanel = (which: 'filter' | 'tags') =>
    setOpenPanel((o) => (o === which ? null : which));

  /** Bold the match inside a server snippet, trusting ITS offsets — never a re-search. */
  const snippetNodes = (r: DocSearchRow) => (
    <>
      {r.snippet.slice(0, r.hlStart)}
      <b>{r.snippet.slice(r.hlStart, r.hlStart + r.hlLen)}</b>
      {r.snippet.slice(r.hlStart + r.hlLen)}
    </>
  );

  /** A title hit has no snippet — bold the first occurrence in the title, same semantics. */
  const titleNodes = (title: string, field: DocSearchRow['field']) => {
    if (field !== 'title' || !searchQuery) return title;
    const at = title.toLowerCase().indexOf(searchQuery);
    if (at < 0) return title;
    return (
      <>
        {title.slice(0, at)}
        <b>{title.slice(at, at + searchQuery.length)}</b>
        {title.slice(at + searchQuery.length)}
      </>
    );
  };

  const shownRows = rows ?? [];
  const kindsOffered = SEARCH_DOC_KINDS.filter((k) => canDocBtn(perm, k));

  return (
    <>
      <div className="search-wrap">
        <div className={searchQuery ? 'searchbox has' : 'searchbox'} id="searchbox">
          <svg className="ico">
            <use href="#i-search" />
          </svg>
          <input
            ref={inputRef}
            id="search-input"
            type="text"
            placeholder="Search nodes…"
            autoComplete="off"
            spellCheck={false}
            value={searchText}
            onChange={(e) => {
              callAction('setSearchText', e.target.value, { frame: true });
              surfaceResults(e.target.value);
            }}
            onFocus={() => surfaceResults(searchText)}
            onKeyDown={(e) => {
              if (e.key !== 'Escape') return;
              // Escape KEEPS its clear-and-blur meaning; the dropdown follows the cleared
              // text out via the eligibility effect above.
              callAction('setSearchText', '', { frame: true });
              e.currentTarget.blur();
            }}
          />
          <button className="sclear" id="search-clear" title="Clear" onClick={clear}>
            <svg className="ico" style={{ width: 14, height: 14 }}>
              <use href="#i-x" />
            </svg>
          </button>
        </div>

        {/* Lit whenever the search is looking at something other than the default set — a
            scope off, or the whole search narrowed to one doc kind. Never invisible. */}
        <button
          ref={filterBtnRef}
          className={!allOn || searchDocKind ? 'sfilter-btn on' : 'sfilter-btn'}
          id="search-filter-btn"
          title="Search in…"
          onClick={(e) => {
            e.stopPropagation();
            togglePanel('filter');
          }}
        >
          <svg className="ico" style={{ width: 15, height: 15 }}>
            <use href="#i-filter" />
          </svg>
        </button>

        {/* The tag filter's face: one dot per selected tag, so an active filter is always
            visible at a glance even while the panel is closed. */}
        <button
          ref={tagBtnRef}
          className={selected.length ? 'sfilter-btn tagdot-btn on' : 'sfilter-btn tagdot-btn'}
          id="tag-filter-btn"
          title="Tags"
          onClick={(e) => {
            e.stopPropagation();
            togglePanel('tags');
          }}
        >
          {selected.length ? (
            <>
              {dotted.map((tid) => (
                <span key={tid} className="tagdot" style={{ background: tags[tid]?.color || '#5b8cff' }} />
              ))}
              {overflowCount > 0 ? <span className="tagdot-more">+{overflowCount}</span> : null}
            </>
          ) : (
            <span className="tagdot neutral" />
          )}
        </button>

        <div
          ref={panelRef}
          className={openPanel === 'filter' ? 'filterpanel show' : 'filterpanel'}
          id="search-filter-panel"
        >
          <label className={searchDocKind ? 'filterrow divider disabled' : 'filterrow divider'}>
            <input
              type="checkbox"
              id="sf-all"
              checked={allOn}
              disabled={!!searchDocKind}
              onChange={toggleAll}
            />
            <span>All</span>
          </label>
          {TEXT_SCOPES.map((key) => (
            <label key={key} className={searchDocKind ? 'filterrow disabled' : 'filterrow'}>
              <input
                type="checkbox"
                id={`sf-${key}`}
                checked={!!scope[key]}
                // A selected doc type takes the text scopes out of play — greyed, not
                // silently ignored, so the narrowing is always visible.
                disabled={!!searchDocKind}
                onChange={() => toggleText(key)}
              />
              <span>{SCOPE_LABEL[key]}</span>
            </label>
          ))}

          <div className="filtergroup">Document type</div>
          <label className="filterrow">
            <input
              type="radio"
              name="sf-doctype"
              id="sf-type-all"
              checked={!searchDocKind}
              onChange={() => callAction('setSearchDocKind', '')}
            />
            <span>All</span>
          </label>
          {/* One radio per documentation kind THIS USER may see — searching by a document
              you may not open is a disclosure, exactly as the old facets were gated. */}
          {kindsOffered.map((k) => (
            <label key={k} className="filterrow" id={`sf-type-${k}-row`}>
              <input
                type="radio"
                name="sf-doctype"
                id={`sf-type-${k}`}
                checked={searchDocKind === k}
                onChange={() => callAction('setSearchDocKind', k)}
              />
              <span>{kindLabel(k)}</span>
            </label>
          ))}
          {searchDocKind ? (
            <div className="filterhint" id="sf-kind-hint">
              Searching only inside {kindLabel(searchDocKind).toLowerCase()} documents
            </div>
          ) : null}
        </div>

        {/* The complete tag pane, as the sidebar carried it: tree, clear, add-a-tag. */}
        <div
          ref={tagPanelRef}
          className={openPanel === 'tags' ? 'tagpanel show' : 'tagpanel'}
          id="tag-panel"
        >
          <div className="side-head">
            <svg className="ico">
              <use href="#i-tag" />
            </svg>
            <h2>Tags</h2>
            <button
              className={tagsPinned ? 'icobtn on' : 'icobtn'}
              id="tag-pin"
              title={tagsPinned ? 'Unpin — close on a click elsewhere' : 'Pin — keep open while you work'}
              aria-pressed={tagsPinned}
              onClick={() => setTagsPinned((p) => !p)}
            >
              <svg className="ico" style={{ width: 15, height: 15 }}>
                <use href="#i-pin" />
              </svg>
            </button>
            <button
              className="icobtn"
              id="tag-clear"
              title="Clear tag filter"
              onClick={() => callAction('setActiveTags', [], { frame: true })}
            >
              <svg className="ico" style={{ width: 15, height: 15 }}>
                <use href="#i-x" />
              </svg>
            </button>
            <button
              className="icobtn"
              id="tag-panel-close"
              title="Close"
              onClick={() => setOpenPanel(null)}
            >
              ×
            </button>
          </div>
          {/* overflow:auto lives on THIS div alone (`.side-body`) — the panel itself must
              never clip, or the drag-and-drop drop bands would vanish under the scrollbar. */}
          <div className="side-body" id="tag-tree" ref={setTagTreeHost}>
            {tagTreeHost ? <TagTree host={tagTreeHost} /> : null}
          </div>
          {/* Edit-mode only, exactly as the sidebar's footer was: the incumbent's
              setEditing() display-toggled it, and `editing` is the same fact derived. */}
          <div className="side-foot" style={editing ? undefined : { display: 'none' }}>
            <input
              ref={tagNewRef}
              id="tag-new"
              placeholder="New tag name…"
              onKeyDown={(e) => {
                if (e.key === 'Enter') addTag();
              }}
            />
            <button className="icobtn" id="tag-add" title="Add tag" onClick={addTag}>
              <svg className="ico">
                <use href="#i-plus" />
              </svg>
            </button>
          </div>
        </div>

        {/* The doc-content results (plan 2.2): documents only — node hits are already the
            map's and the counter's answer. */}
        <div
          ref={resultsRef}
          className={openPanel === 'results' ? 'searchresults show' : 'searchresults'}
          id="search-results"
        >
          {/* NODES FIRST, drawn as the map draws them (V0.46): the swatch and the node's own
              tint, so a result is recognisably the thing you will land on rather than a line
              of text about it. Their own class, never `.sr-row` — the documents below are
              what that class means, here and in the specs that drive this panel. */}
          {nodeRows.map((id) => {
            const n = nodes[id];
            if (!n) return null;
            const paint = n.color || 'var(--accent)';
            return (
              <div
                key={id}
                className="sr-noderow"
                onClick={() => openNodeRow(id)}
                title={n.label}
                style={nodeTint(n.color, isLight)}
              >
                <span className="swatch" style={{ background: paint }} />
                <span className="sr-nodename">{titleNodes(n.label, 'title')}</span>
              </div>
            );
          })}
          {nodeHits.length > nodeRows.length ? (
            <div className="sr-more">
              +{nodeHits.length - nodeRows.length} more nodes — they are lit up on the map
            </div>
          ) : null}
          {nodeRows.length && fetchEligible ? <div className="sr-sep" /> : null}
          {loading && rows === null ? (
            <div className="sr-status">Searching documents…</div>
          ) : null}
          {rows !== null && shownRows.length === 0 && !loading ? (
            <div className="sr-status">No documents match</div>
          ) : null}
          {shownRows.map((r) => {
            // TWO COLOURS, answering two different questions (V0.46): the node's, down the
            // left edge, says which part of the map this page belongs to — the same paint the
            // node rows above carry, so the two halves of the dropdown read as one list. The
            // kind's pill says what sort of page it is, in the bean colours the map's rows,
            // the doc-nav band and the popup already use.
            const owner = nodes[r.nodeId];
            const paint = owner?.color || 'var(--ink-faint)';
            const perm = docPermOnNode(owner, r.docId);
            return (
              <div
                key={`${r.docId}:${r.field}`}
                className="sr-row"
                onClick={() => openRow(r)}
                title={docTitles[r.docId] || r.title}
                style={{ borderLeftColor: paint }}
              >
                <svg className="ico">
                  <use href={`#${docIconId(docTypes, r.docId)}`} />
                </svg>
                <div className="sr-main">
                  <div className="sr-title">
                    {titleNodes(docTitles[r.docId] || r.title, r.field)}
                    <span className="sr-node">
                      <span className="sr-nodedot" style={{ background: paint }} />
                      {owner?.label ?? r.nodeLabel ?? ''}
                    </span>
                  </div>
                  {r.field === 'content' ? <div className="sr-snip">{snippetNodes(r)}</div> : null}
                </div>
                <span
                  className="sr-kind"
                  style={{
                    color: kindColorOf(perm),
                    borderColor: `color-mix(in srgb, ${kindColorOf(perm)} 45%, transparent)`,
                    background: `color-mix(in srgb, ${kindColorOf(perm)} 14%, transparent)`,
                  }}
                >
                  {kindLabelOf(perm)}
                </span>
              </div>
            );
          })}
          {rows !== null && total > shownRows.length ? (
            <div className="sr-more">+{total - shownRows.length} more — refine your search</div>
          ) : null}
        </div>
      </div>

      <span className="search-count" id="search-count">
        {active ? (matches ? `${matches} match${matches > 1 ? 'es' : ''}` : 'no matches') : ''}
      </span>
    </>
  );
}
